import type { RuleFiringBatch } from '@tunasong/schemas'
import type { FC, ReactNode } from 'react'
import { formatLastFired } from './automation-summary.js'

/** The feed answers "what did this rule do lately", not "everything it ever did". */
const MAX_RUNS_SHOWN = 10

export interface RuleHistoryProps {
  batches: RuleFiringBatch[]
  /** True while the firing-batch data behind `batches` has not loaded yet. An empty `batches`
   * is indistinguishable from "never fired" otherwise, so a freshly selected rule would flash
   * "Hasn't run yet" during the load -- render a neutral loading state instead of asserting that. */
  loading?: boolean
  /**
   * Renders a navigable link for the entity that triggered a run. Injected so this
   * component stays free of data hooks -- the page supplies an `EntityLinkById`,
   * tests supply a plain node. When absent, rows fall back to the summary alone.
   */
  renderEntityLink?: (entityId: string) => ReactNode
  /** Injected so relative times are testable without mocking the clock. */
  now?: Date
  className?: string
}

const describeBatch = (batch: RuleFiringBatch): string => {
  const count = batch.appliedActions.length
  if (count === 0) {
    return 'Ran, changed nothing'
  }
  return `Changed ${count} item${count === 1 ? '' : 's'}`
}

export const RuleHistory: FC<RuleHistoryProps> = props => {
  const { batches, loading = false, renderEntityLink, now = new Date(), className } = props

  // firedAt is fixed-format ISO-8601 (new Date().toISOString()), so code-point order equals chronological order.
  // Plain comparison avoids unnecessary locale dependency.
  const newestFirst = [...batches].sort((a, b) => (a.firedAt < b.firedAt ? 1 : -1))
  const shown = newestFirst.slice(0, MAX_RUNS_SHOWN)

  return (
    <div className={className}>
      {loading ? (
        <p>Loading…</p>
      ) : batches.length === 0 ? (
        <p>Hasn&apos;t run yet</p>
      ) : (
        <>
          <ul>
            {shown.map(batch => (
              <li key={batch.batchId}>
                {renderEntityLink?.(batch.triggeringEntityId)}
                {describeBatch(batch)} · {formatLastFired(batch.firedAt, now)}
              </li>
            ))}
          </ul>
          {newestFirst.length > shown.length ? (
            <p>
              Showing the last {shown.length} of {newestFirst.length} runs.
            </p>
          ) : null}
        </>
      )}
      {/* Reachable in the empty case too: firing batches carry a 90-day TTL, so a rule that
       * last fired 100 days ago lands here with no batches left -- the note is what explains
       * that its history simply aged out, rather than the rule never having run at all. */}
      <p>History and undo are kept for 90 days.</p>
    </div>
  )
}
